/**
 * The category surface's pure half (P7-T1).
 *
 * A category is a label and nothing else: it stores no money and no total, so every figure here is
 * either the API's own derivation (the read already groups the entries by category *and currency*
 * and sums each row's signed amount once) or a rendering of it in exact minor units. Nothing in
 * this module adds two currencies, and nothing re-decides what a category total is —
 * `@saldo/domain`'s `categoryTotalMinor` is that rule for the pure layers, and the API's SQL is the
 * same rule for the stored rows.
 *
 * Two things live here rather than in JSX, so the fast suite can assert them:
 *
 *   - the **body of each action** — create, rename, the reversible archive — validated before a
 *     round trip, with the API still the authority;
 *   - the **named refusal**. A reference-safe deletion is refused `409` with the blocking reference
 *     in the envelope's `fieldErrors` (`path: '/references'`, `code: 'entry'`), and the surface has
 *     to say *which* reference blocked it and what the reversible alternative is. That sentence is
 *     built here, once, for both the category and the account surface.
 */

import type {
  CategoryBody,
  CategoryCreateRequest,
  CategoryPatchRequest,
  FieldError,
} from '@saldo/contracts';

import { LedgerRequestError, type LedgerFailureKind } from './client';
import type { Mapped } from './forms';
import { formatMoneyMagnitude } from './money';

/** The name bound the API enforces (`categorySchema` / `categoryCreateRequestSchema`). */
export const CATEGORY_NAME_LIMIT = 120;

/** One category's whole figure in one currency, as the read derives it. */
export interface CategoryTotalView {
  readonly currency: string;
  readonly minorUnits: bigint;
}

/**
 * What an action needs from a category: its identity, its current name, its visibility, and the
 * revision the write has to state as `If-Match`. Narrower than the whole view on purpose, so a
 * control receives a target rather than a figure list.
 */
export interface CategoryTarget {
  readonly id: string;
  readonly name: string;
  readonly archived: boolean;
  readonly revision: string;
}

/**
 * One category as the surface renders it.
 *
 * `archived` is carried rather than filtered away, because archiving is *visibility*: the surface
 * decides what to show, and the totals are the same either way. Nothing here re-sums.
 */
export interface CategoryView extends CategoryTarget {
  readonly entryCount: number;
  readonly totals: readonly CategoryTotalView[];
}

export function categoryView(category: CategoryBody): CategoryView {
  return {
    id: category.id,
    name: category.name,
    archived: category.archived,
    revision: category.revision,
    entryCount: category.entryCount,
    totals: category.totals.map((total) => ({
      currency: total.currency,
      minorUnits: BigInt(total.minorUnits),
    })),
  };
}

export function categoryViews(categories: readonly CategoryBody[]): CategoryView[] {
  return categories.map(categoryView);
}

/** The categories a view shows, active first, each group by name — never re-ordered by money. */
export function categoriesFor(view: readonly CategoryView[], archived: boolean): CategoryView[] {
  return view
    .filter((category) => category.archived === archived)
    .sort((left, right) => left.name.localeCompare(right.name) || left.id.localeCompare(right.id));
}

/**
 * A category's figure as text: every currency it carries, kept apart.
 *
 * `''` when the category has no entries at all — *uncategorized* entries do not belong to any
 * category, so a category with none shows no figure rather than a `0.00` that would read as money.
 */
export function formatCategoryTotals(category: CategoryView): string {
  return category.totals
    .map((total) => formatMoneyMagnitude(total.minorUnits, total.currency))
    .join(' · ');
}

/* ------------------------------------------------------------------ the actions --- */

function categoryName(name: string): Mapped<string> {
  const trimmed = name.trim();
  if (trimmed === '') return { ok: false, error: 'Enter a category name.' };
  if (trimmed.length > CATEGORY_NAME_LIMIT) {
    return { ok: false, error: `Keep the name under ${CATEGORY_NAME_LIMIT} characters.` };
  }
  return { ok: true, body: trimmed };
}

/** `POST /api/v1/categories` — create, active. */
export function toCategoryCreate(name: string): Mapped<CategoryCreateRequest> {
  const parsed = categoryName(name);
  if (!parsed.ok) return parsed;
  return { ok: true, body: { name: parsed.body } };
}

/** `PATCH /api/v1/categories/{id}` — rename. */
export function toCategoryRename(name: string): Mapped<CategoryPatchRequest> {
  const parsed = categoryName(name);
  if (!parsed.ok) return parsed;
  return { ok: true, body: { name: parsed.body } };
}

/**
 * `PATCH /api/v1/categories/{id}` — the reversible archive.
 *
 * One boolean, so *unhide* is this same call with `false`. It is a metadata write: it touches no
 * entry, which is why an archived category keeps its history and its totals.
 */
export function toCategoryArchive(archived: boolean): CategoryPatchRequest {
  return { archived };
}

/** The category an ordinary entry carries, or the first-class *uncategorized* state. */
export type CategoryChoice = { readonly kind: 'none' } | { readonly kind: 'category'; readonly id: string };

/** The select's value for a category choice: `''` is the uncategorized option. */
export function categoryChoiceValue(choice: CategoryChoice): string {
  return choice.kind === 'none' ? '' : choice.id;
}

/** The choice a select value means. An empty value is *uncategorized*, never a missing field. */
export function categoryChoiceFrom(value: string): CategoryChoice {
  return value === '' ? { kind: 'none' } : { kind: 'category', id: value };
}

/* ----------------------------------------------------------- the named refusal --- */

/** What a blocking reference is, in words. `kind` names the reference without naming the row. */
const REFERENCE_WORDS: Record<string, { readonly one: string; readonly many: string }> = {
  entry: { one: 'an entry', many: 'entries' },
  account: { one: 'an account', many: 'accounts' },
  category: { one: 'a category', many: 'categories' },
  installmentGroup: { one: 'an installment group', many: 'installment groups' },
  transfer: { one: 'a payment', many: 'payments' },
};

/** One reference the envelope named, in words. */
export interface NamedReference {
  readonly code: string;
  readonly count: number;
  readonly label: string;
}

/**
 * The references a refusal named, or `[]` when it named none.
 *
 * Only the envelope's own `/references` entries are read; a `fieldErrors` list is a list of codes
 * and paths the application itself produced, so this renders no server text.
 */
export function namedReferences(error: LedgerRequestError): NamedReference[] {
  return error.fieldErrors
    .filter((field: FieldError) => field.path === '/references')
    .map((field: FieldError) => {
      const words = REFERENCE_WORDS[field.code] ?? { one: field.code, many: field.code };
      return { code: field.code, count: 1, label: words.one };
    });
}

/** What a reference-safe deletion is about, so the sentence can name the right alternative. */
export interface DeletionSubject {
  readonly what: 'category' | 'account';
  /** The name the surface knows, or `null` when it does not. */
  readonly name: string | null;
}

/**
 * The sentence a reference-safe deletion's refusal is shown with.
 *
 * The refusal is the point rather than an error to hide: a referenced category or account cannot be
 * removed without destroying history, so the surface says what still references it and offers the
 * reversible alternative the API has — archiving. A refusal that named no reference falls back to
 * the generic vocabulary, and a failure that was not a refusal at all says so instead of pretending.
 */
export function describeReferenceRefusal(error: LedgerRequestError, subject: DeletionSubject): string {
  const alternative =
    subject.what === 'category'
      ? 'Archive it instead — the entries keep their history and the category keeps its totals.'
      : 'Hide it instead — its entries and its balance are untouched.';
  const name = subject.name === null ? `This ${subject.what}` : `“${subject.name}”`;
  const references = namedReferences(error);

  // The wire carries one generic code per class and no reason at all (register §7, "Error
  // envelope"), so the *structure* of the refusal is what identifies it: the only refusal that
  // fills `fieldErrors` at `path: '/references'` is a reference-safe deletion that was blocked
  // (`apps/api/src/ledger/categories.ts` and `service.ts`). Reading a code the API never sends
  // would leave this sentence unreachable and show a person the generic one instead.
  if (references.length !== 0) {
    const list = references.map((reference) => reference.label).join(' and ');
    return `${name} is still carried by ${list}, so it cannot be deleted. ${alternative}`;
  }
  return describeRefusal(error.kind);
}

/** The generic vocabulary, kept in one place so every surface words a failure the same way. */
export function describeRefusal(kind: LedgerFailureKind): string {
  switch (kind) {
    case 'unauthenticated':
      return 'Your session ended. Sign in again to continue.';
    case 'forbidden':
      return 'The request was refused. Reload the page and try again.';
    case 'not-found':
      return 'That record is no longer available.';
    case 'revision-required':
      return 'This record changed since the page opened. Reload it before saving.';
    case 'revision-stale':
      return 'This record was changed elsewhere. Reload it to see the current version.';
    case 'intent-conflict':
      return 'That operation key was already used for a different change. Start a new save.';
    case 'rejected':
      return 'The change was refused. Check the fields and try again.';
    case 'throttled':
      return 'Too many requests. Wait a moment and try again.';
    case 'contract':
      return 'The server answered something this application does not understand.';
    case 'unavailable':
      return 'The server could not be reached. Try again.';
  }
}
